import React, { useState, useEffect } from 'react'
import { useScrollAnimation } from '../hooks/useScrollAnimation'
import { getActiveJobs } from '../utils/jobStorage'

const Jobs = () => {
  const { ref, isVisible } = useScrollAnimation()
  const [jobs, setJobs] = useState([])
  const [filteredJobs, setFilteredJobs] = useState([])
  const [sectors, setSectors] = useState([])
  const [activeFilter, setActiveFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [refreshKey, setRefreshKey] = useState(0)

  // Refresh jobs when component mounts or when refreshKey changes
  useEffect(() => {
    fetchJobs()
  }, [refreshKey])

  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredJobs(jobs)
    } else {
      setFilteredJobs(jobs.filter(job => job.sector === activeFilter))
    }
  }, [activeFilter, jobs])

  const fetchJobs = async () => {
    try {
      // Use the jobStorage utility
      const data = getActiveJobs()
      setJobs(data)
      setLoading(false)
    } catch {
      setJobs([])
      setLoading(false)
    }
  }

  // Listen for storage changes (from admin panel)
  useEffect(() => {
    const handleJobsChanged = () => {
      setRefreshKey(prev => prev + 1)
    }

    window.addEventListener('jobsChanged', handleJobsChanged)
    
    return () => {
      window.removeEventListener('jobsChanged', handleJobsChanged)
    }
  }, [])

  useEffect(() => {
    // Extract unique sectors
    const uniqueSectors = [...new Set(jobs.map(job => job.sector))]
    setSectors(uniqueSectors)
  }, [jobs])

  const handleFilterClick = (sector) => {
    setActiveFilter(sector)
  }

  if (loading) {
    return (
      <section id="jobs" className="jobs section" ref={ref}>
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="jobs" className="jobs section" ref={ref}>
      <div className="container">
        <div className="section-header">
          <div className={`section-tag ${isVisible ? 'animate-fadeInUp' : ''}`}>
            Current Roles
          </div>
          <h2 className={`${isVisible ? 'animate-fadeInUp' : ''}`}>
            Open <span className="gradient-text">Opportunities</span>
          </h2>
        </div>

        {/* Filters */}
        {sectors.length > 0 && (
          <div className={`jobs-filters ${isVisible ? 'animate-fadeInUp' : ''}`}>
            <button 
              className={`jobs-filter ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => handleFilterClick('all')}
            >
              All Jobs
            </button>
            {sectors.map((sector) => (
              <button 
                key={sector}
                className={`jobs-filter ${activeFilter === sector ? 'active' : ''}`}
                onClick={() => handleFilterClick(sector)}
              >
                {sector}
              </button>
            ))}
          </div>
        )}

        {/* Job Cards */}
        <div className="jobs-grid">
          {filteredJobs.length > 0 ? (
            filteredJobs.map((job, index) => (
              <div 
                key={job.id}
                className={`job-card ${isVisible ? 'animate-fadeInUp' : ''}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="job-body">
                  {job.sector && (
                    <div className="job-sector-label">
                      <i className="fas fa-briefcase"></i>
                      {job.sector}
                    </div>
                  )}
                  <h3 className="job-title">{job.title}</h3>
                  {job.company && <div className="job-company">{job.company}</div>}
                  <div className="job-meta">
                    <span className="job-meta-item">
                      <i className="fas fa-map-marker-alt"></i>
                      {job.location}
                    </span>
                    <span className="job-meta-item">
                      <i className="fas fa-clock"></i>
                      {job.type}
                    </span>
                    {job.salary && (
                      <span className="job-meta-item job-salary">
                        <i className="fas fa-dollar-sign"></i>
                        {job.salary}
                      </span>
                    )}
                  </div>
                  {job.links && job.links.length > 0 && (
                    <div className="job-links">
                      {job.links.map((link, linkIndex) => (
                        <a
                          key={linkIndex}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={linkIndex === 0 ? 'btn btn-gold btn-sm' : 'btn btn-secondary btn-sm'}
                        >
                          {link.text || 'Apply Now'}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className={`jobs-empty ${isVisible ? 'animate-fadeInUp' : ''}`}>
              <i className="fas fa-briefcase"></i>
              <h3>No jobs available right now</h3>
              <p>Check back soon!</p>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default Jobs
